package com.schoolconnect.app.feature.schoolusers

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.feature.common.compareBySmartText
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.ban
import ischoolconnect.sharedui.generated.resources.chevron_left
import ischoolconnect.sharedui.generated.resources.chevron_right
import ischoolconnect.sharedui.generated.resources.circle_check
import ischoolconnect.sharedui.generated.resources.eye
import ischoolconnect.sharedui.generated.resources.filter
import ischoolconnect.sharedui.generated.resources.refresh_cw
import ischoolconnect.sharedui.generated.resources.more_vertical
import ischoolconnect.sharedui.generated.resources.pencil
import ischoolconnect.sharedui.generated.resources.trash
import ischoolconnect.sharedui.generated.resources.search
import org.jetbrains.compose.resources.painterResource
import com.schoolconnect.app.designsystem.AppLabeledDropdownField
import com.schoolconnect.app.designsystem.list.AdminInfoBanner
import com.schoolconnect.app.designsystem.list.AdminListFooter
import com.schoolconnect.app.designsystem.list.AdminListHeaderColumn
import com.schoolconnect.app.designsystem.list.AdminListPagination
import com.schoolconnect.app.designsystem.list.AdminListSearchField
import com.schoolconnect.app.designsystem.list.AdminListSortColumn
import com.schoolconnect.app.designsystem.list.AdminListSortHeaderRow
import com.schoolconnect.app.designsystem.list.AdminListTableBodyText
import com.schoolconnect.app.designsystem.list.AdminListToolbar
import com.schoolconnect.app.designsystem.list.AdminListToolbarButton
import com.schoolconnect.app.designsystem.list.AdminListRowsPerPageButton
import com.schoolconnect.app.designsystem.list.AdminListRowActionBar
import com.schoolconnect.app.designsystem.list.AdminStandardRowActionMenu
import com.schoolconnect.app.designsystem.list.onViewDoubleClick
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppDangerSoft
import com.schoolconnect.app.designsystem.AppInputBorder
import com.schoolconnect.app.designsystem.AppLine
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.AppPurpleSoft
import com.schoolconnect.app.designsystem.AppPurpleText
import com.schoolconnect.app.designsystem.AppSecondaryBackground
import com.schoolconnect.app.designsystem.AppSoftSurface
import com.schoolconnect.app.designsystem.AppSurface
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.designsystem.AppSuccessSoft
import com.schoolconnect.app.designsystem.AppInfoText

internal val SchoolUsersBackground = AppSecondaryBackground
internal val SchoolUsersSurface = AppSurface
internal val SchoolUsersBorder = AppBorder
internal val SchoolUsersLine = AppLine
internal val SchoolUsersNavy = AppNavy
internal val SchoolUsersMuted = AppMuted
internal val SchoolUsersPrimary = AppPrimary
internal val SchoolUsersSuccess = AppSuccess
internal val SchoolUsersSuccessBg = AppSuccessSoft
internal val SchoolUsersDanger = AppDanger
internal val SchoolUsersDangerBg = AppDangerSoft
internal val SchoolUsersRoleBlueBg = AppPrimarySoft
internal val SchoolUsersRoleBlueText = AppInfoText
internal val SchoolUsersRolePurpleBg = AppPurpleSoft
internal val SchoolUsersRolePurpleText = AppPurpleText
internal val SchoolUsersInputBorder = AppInputBorder
internal val SchoolUsersOrange = Color(0xFFFF7A00)

internal fun sortedSchoolUsers(
    users: List<SchoolUserRowUi>,
    field: SchoolUsersSortField,
    ascending: Boolean
): List<SchoolUserRowUi> = users.sortedWith(schoolUsersComparator(field, ascending))

internal fun schoolUsersTableShimmerColumns(): List<AdminListHeaderColumn> = listOf(
    AdminListHeaderColumn(label = "School", weight = 1.6f),
    AdminListHeaderColumn(label = "User", weight = 1.25f),
    AdminListHeaderColumn(label = "Role", weight = 0.9f),
    AdminListHeaderColumn(label = "Mobile", weight = 0.9f),
    AdminListHeaderColumn(label = "Status", weight = 0.9f),
    AdminListHeaderColumn(label = "Actions", weight = 0.95f)
)

internal fun schoolUsersComparator(
    field: SchoolUsersSortField,
    ascending: Boolean
): Comparator<SchoolUserRowUi> {
    val comparator = when (field) {
        SchoolUsersSortField.USER -> compareBySmartText<SchoolUserRowUi> { it.name }
        SchoolUsersSortField.SCHOOL -> compareBySmartText<SchoolUserRowUi> { it.school }
        SchoolUsersSortField.MOBILE -> compareBySmartText<SchoolUserRowUi> { it.mobile }
        SchoolUsersSortField.EMAIL -> compareBySmartText<SchoolUserRowUi> { it.email }
        SchoolUsersSortField.ROLE -> compareBy<SchoolUserRowUi> {
            when (it.role) {
                SchoolUserRoleUi.SCHOOL_ADMIN -> 0
                SchoolUserRoleUi.SCHOOL_MANAGEMENT -> 1
            }
        }
        SchoolUsersSortField.STATUS -> compareBy<SchoolUserRowUi> {
            when (it.status) {
                SchoolUserStatusUi.ACTIVE -> 0
                SchoolUserStatusUi.INACTIVE -> 1
            }
        }
    }
    return if (ascending) comparator else comparator.reversed()
}

internal fun roleLabelForFilter(role: SchoolUserRoleUi): String = when (role) {
    SchoolUserRoleUi.SCHOOL_ADMIN -> "School Admin"
    SchoolUserRoleUi.SCHOOL_MANAGEMENT -> "School Management"
}

internal fun schoolUsersFilterLabels(state: SchoolUsersUiState): List<String> = buildList {
    if (state.searchQuery.isNotBlank()) add("Search: ${state.searchQuery.trim()}")
    if (state.schoolFilterId.isNotBlank()) {
        val schoolName = state.schoolFilterName.ifBlank {
            state.schoolOptions.firstOrNull { it.id == state.schoolFilterId }?.name.orEmpty()
        }
        add("School: ${schoolName.ifBlank { state.schoolFilterId }}")
    }
    if (state.createdFrom.isNotBlank() || state.createdTo.isNotBlank()) {
        val from = state.createdFrom.ifBlank { "Any" }
        val to = state.createdTo.ifBlank { "Any" }
        add("Created: $from to $to")
    }
}

@Composable
internal fun SchoolUsersSummaryCard(
    title: String,
    value: String,
    subtitle: String,
    icon: Painter,
    accent: Color,
    accentBg: Color,
    subtitleColor: Color,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier.height(128.dp),
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = SchoolUsersSurface),
        border = androidx.compose.foundation.BorderStroke(1.dp, SchoolUsersBorder)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 22.dp, vertical = 22.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(20.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(70.dp)
                    .background(accentBg, RoundedCornerShape(10.dp)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = icon,
                    contentDescription = title,
                    tint = accent,
                    modifier = Modifier.size(34.dp)
                )
            }
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.bodyMedium.copy(fontSize = 14.sp),
                    color = SchoolUsersNavy,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.headlineSmall.copy(fontSize = 30.sp),
                    color = SchoolUsersNavy,
                    fontWeight = FontWeight.ExtraBold,
                    letterSpacing = (-0.02).sp
                )
                Text(
                    text = subtitle,
                    style = MaterialTheme.typography.bodySmall.copy(fontSize = 13.sp),
                    color = subtitleColor,
                    fontWeight = FontWeight.SemiBold
                )
            }
        }
    }
}

@Composable
internal fun SchoolUsersRoleBadge(role: SchoolUserRoleUi) {
    val label = when (role) {
        SchoolUserRoleUi.SCHOOL_ADMIN -> "School Admin"
        SchoolUserRoleUi.SCHOOL_MANAGEMENT -> "School Management"
    }
    AdminListTableBodyText(text = label, color = SchoolUsersNavy)
}

@Composable
internal fun SchoolUsersStatusBadge(status: SchoolUserStatusUi) {
    val (label, textColor) = when (status) {
        SchoolUserStatusUi.ACTIVE -> "Active" to SchoolUsersSuccess
        SchoolUserStatusUi.INACTIVE -> "Inactive" to SchoolUsersMuted
    }
    Text(
        text = label,
        color = textColor,
        fontSize = 14.sp,
        fontWeight = FontWeight.Normal
    )
}

@Composable
internal fun SchoolUsersSortLabel(
    text: String,
    selected: Boolean,
    ascending: Boolean,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.labelLarge.copy(fontSize = 12.sp),
            color = SchoolUsersNavy,
            fontWeight = FontWeight.ExtraBold
        )
        Text(
            text = if (selected) if (ascending) "↑" else "↓" else "↕",
            style = MaterialTheme.typography.labelMedium.copy(fontSize = 11.sp),
            color = if (selected) SchoolUsersPrimary else SchoolUsersMuted,
            fontWeight = FontWeight.Bold
        )
    }
}

@Composable
internal fun SchoolUsersTableHeader(
    sortField: SchoolUsersSortField,
    sortAscending: Boolean,
    onSortChanged: (SchoolUsersSortField) -> Unit
) {
    AdminListSortHeaderRow(
        columns = listOf(
            AdminListSortColumn(
                label = "School",
                weight = 1.6f,
                selected = sortField == SchoolUsersSortField.SCHOOL,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolUsersSortField.SCHOOL) }
            ),
            AdminListSortColumn(
                label = "User",
                weight = 1.25f,
                selected = sortField == SchoolUsersSortField.USER,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolUsersSortField.USER) }
            ),
            AdminListSortColumn(
                label = "Role",
                weight = 0.9f,
                selected = sortField == SchoolUsersSortField.ROLE,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolUsersSortField.ROLE) }
            ),
            AdminListSortColumn(
                label = "Mobile",
                weight = 0.9f,
                selected = sortField == SchoolUsersSortField.MOBILE,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolUsersSortField.MOBILE) }
            ),
            AdminListSortColumn(
                label = "Status",
                weight = 0.9f,
                selected = sortField == SchoolUsersSortField.STATUS,
                ascending = sortAscending,
                onClick = { onSortChanged(SchoolUsersSortField.STATUS) }
            )
        ),
        actionsWeight = 0.95f,
        actionsAlignment = Alignment.Center,
        height = 44.dp,
        horizontalPadding = 24.dp
    )
}

@Composable
private fun RowScope.SchoolUsersSortHeaderCell(
    label: String,
    weight: Float,
    selected: Boolean,
    ascending: Boolean,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .weight(weight)
            .clickable(onClick = onClick)
            .padding(end = 6.dp)
    ) {
        SchoolUsersSortLabel(text = label, selected = selected, ascending = ascending)
    }
}

@Composable
internal fun SchoolUsersRow(
    user: SchoolUserRowUi,
    isActionMenuOpen: Boolean,
    onActionMenuToggle: () -> Unit,
    onActionMenuDismiss: () -> Unit,
    onViewDetails: () -> Unit,
    onEditUser: () -> Unit,
    onToggleStatus: () -> Unit,
    onDeleteUser: () -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        HorizontalDivider(color = SchoolUsersLine)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .onViewDoubleClick(onViewDetails)
                .height(52.dp)
                .padding(horizontal = 24.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(modifier = Modifier.weight(1.6f)) {
                AdminListTableBodyText(
                    text = user.school,
                    color = SchoolUsersNavy
                )
            }
            Box(modifier = Modifier.weight(1.25f)) {
                AdminListTableBodyText(
                    text = user.name,
                    color = SchoolUsersNavy
                )
            }
            Box(modifier = Modifier.weight(0.9f)) {
                SchoolUsersRoleBadge(role = user.role)
            }
            Box(modifier = Modifier.weight(0.9f)) {
                AdminListTableBodyText(
                    text = user.mobile,
                    color = SchoolUsersNavy
                )
            }
            Box(modifier = Modifier.weight(0.9f)) {
                SchoolUsersStatusBadge(status = user.status)
            }
            Box(
                modifier = Modifier.weight(0.95f),
                contentAlignment = Alignment.Center
            ) {
                AdminListRowActionBar(
                    isActive = user.status == SchoolUserStatusUi.ACTIVE,
                    expanded = isActionMenuOpen,
                    onExpandedChange = { onActionMenuToggle() },
                    onDismissRequest = onActionMenuDismiss,
                    onView = onViewDetails,
                    onEdit = onEditUser,
                    onToggleStatus = onToggleStatus,
                    onDelete = onDeleteUser,
                    width = 196.dp,
                    surfaceColor = SchoolUsersSurface,
                    borderColor = SchoolUsersBorder
                )
            }
        }
    }
}

@Composable
internal fun SchoolUsersActionMenu(
    isActive: Boolean,
    onDismissRequest: () -> Unit,
    onViewDetails: () -> Unit,
    onEditUser: () -> Unit,
    onToggleStatus: () -> Unit,
    onDeleteUser: () -> Unit
) {
    AdminStandardRowActionMenu(
        isActive = isActive,
        onDismissRequest = onDismissRequest,
        onView = onViewDetails,
        onEdit = onEditUser,
        onToggleStatus = onToggleStatus,
        onDelete = onDeleteUser,
        width = 196.dp,
        surfaceColor = SchoolUsersSurface,
        borderColor = SchoolUsersBorder
    )
}

@Composable
internal fun SchoolUsersToolbar(
    searchQuery: String,
    onSearchQueryChanged: (String) -> Unit,
    role: String,
    onRoleSelected: (String) -> Unit,
    status: String,
    onStatusSelected: (String) -> Unit,
    onFilterClick: () -> Unit,
    onResetClick: () -> Unit
) {
    AdminListToolbar(
        searchQuery = searchQuery,
        onSearchQueryChanged = onSearchQueryChanged,
        searchPlaceholder = "Search by name, mobile or email...",
        searchWidth = 370.dp,
        surfaceColor = SchoolUsersSurface,
        borderColor = SchoolUsersBorder,
        searchIconTint = SchoolUsersMuted,
        searchTextColor = SchoolUsersNavy,
        searchCursorColor = SchoolUsersPrimary,
        inlineContent = {
            AppLabeledDropdownField(
                label = "",
                value = role.ifBlank { "All Roles" },
                options = listOf("School Admin", "School Management"),
                onSelected = onRoleSelected,
                modifier = Modifier.width(200.dp),
                allOptionLabel = "All Roles"
            )
            AppLabeledDropdownField(
                label = "",
                value = if (status.isBlank() || status == "All") "All Statuses" else status,
                options = listOf("Active", "Inactive"),
                onSelected = onStatusSelected,
                modifier = Modifier.width(160.dp),
                allOptionLabel = "All Statuses"
            )
        }
    ) {
        AdminListToolbarButton(
            label = "Reset",
            icon = painterResource(Res.drawable.refresh_cw),
            onClick = onResetClick,
            width = 112.dp,
            borderColor = SchoolUsersPrimary,
            contentColor = SchoolUsersPrimary
        )
        AdminListToolbarButton(
            label = "More Filters",
            icon = painterResource(Res.drawable.filter),
            onClick = onFilterClick,
            width = 148.dp,
            borderColor = SchoolUsersPrimary,
            contentColor = SchoolUsersPrimary
        )
    }
}

@Composable
private fun SchoolUsersSearchField(
    value: String,
    onValueChange: (String) -> Unit
) {
    AdminListSearchField(
        value = value,
        onValueChange = onValueChange,
        placeholder = "Search by name, mobile or email...",
        width = 370.dp,
        surfaceColor = SchoolUsersSurface,
        borderColor = SchoolUsersBorder,
        iconTint = SchoolUsersMuted,
        textColor = SchoolUsersNavy,
        cursorColor = SchoolUsersPrimary
    )
}

@Composable
private fun SchoolUsersToolbarButton(
    label: String,
    icon: Painter,
    onClick: () -> Unit,
    width: androidx.compose.ui.unit.Dp
) {
    AdminListToolbarButton(
        label = label,
        icon = icon,
        onClick = onClick,
        width = width,
        borderColor = SchoolUsersBorder,
        contentColor = SchoolUsersPrimary,
        iconSize = 19.dp
    )
}

@Composable
private fun SchoolUsersRowsPerPageButton(
    pageSize: Int,
    expanded: Boolean,
    onClick: () -> Unit
) {
    AdminListRowsPerPageButton(
        pageSize = pageSize,
        expanded = expanded,
        onClick = onClick,
        width = 82.dp,
        borderColor = SchoolUsersBorder,
        surfaceColor = SchoolUsersSurface,
        contentColor = SchoolUsersNavy,
        iconTint = SchoolUsersMuted
    )
}

@Composable
private fun SchoolUsersFilterOption(
    label: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    DropdownMenuItem(
        text = {
            Text(
                text = label,
                fontWeight = if (selected) FontWeight.ExtraBold else FontWeight.Medium
            )
        },
        onClick = onClick
    )
}

@Composable
internal fun SchoolUsersFooter(
    pageText: String,
    pageIndex: Int,
    pageCount: Int,
    onPageChanged: (Int) -> Unit,
    pageSize: Int,
    onPageSizeChanged: (Int) -> Unit,
    rowsPerPageExpanded: Boolean,
    onRowsPerPageToggle: () -> Unit,
    onRowsPerPageDismiss: () -> Unit
) {
    AdminListFooter(
        pageText = pageText,
        pageIndex = pageIndex,
        pageCount = pageCount,
        onPageChanged = onPageChanged,
        pageSize = pageSize,
        rowsPerPageExpanded = rowsPerPageExpanded,
        onRowsPerPageToggle = onRowsPerPageToggle,
        onRowsPerPageDismiss = onRowsPerPageDismiss,
        onPageSizeChanged = onPageSizeChanged,
        borderColor = SchoolUsersBorder,
        footerColor = SchoolUsersMuted
    )
}

@Composable
internal fun SchoolUsersPagination(
    pageIndex: Int,
    pageCount: Int,
    onPageChanged: (Int) -> Unit
) {
    AdminListPagination(
        pageIndex = pageIndex,
        pageCount = pageCount,
        onPageChanged = onPageChanged,
        borderColor = SchoolUsersBorder,
        surfaceColor = SchoolUsersSurface,
        primaryColor = SchoolUsersPrimary,
        navyColor = SchoolUsersNavy,
        mutedColor = SchoolUsersMuted
    )
}

private sealed interface SchoolUsersPageButton {
    data class Number(val number: Int) : SchoolUsersPageButton
    data object Ellipsis : SchoolUsersPageButton
}

private fun buildSchoolUsersPagination(pageIndex: Int, pageCount: Int): List<SchoolUsersPageButton> {
    if (pageCount <= 7) {
        return (1..pageCount).map { SchoolUsersPageButton.Number(it) }
    }

    val currentPage = pageIndex + 1
    return when {
        currentPage <= 4 -> listOf(
            1, 2, 3, 4, 5
        ).map { SchoolUsersPageButton.Number(it) } + listOf(
            SchoolUsersPageButton.Ellipsis,
            SchoolUsersPageButton.Number(pageCount)
        )

        currentPage >= pageCount - 3 -> listOf(
            SchoolUsersPageButton.Number(1),
            SchoolUsersPageButton.Ellipsis
        ) + ((pageCount - 4)..pageCount).map { SchoolUsersPageButton.Number(it) }

        else -> listOf(
            SchoolUsersPageButton.Number(1),
            SchoolUsersPageButton.Ellipsis,
            SchoolUsersPageButton.Number(currentPage - 1),
            SchoolUsersPageButton.Number(currentPage),
            SchoolUsersPageButton.Number(currentPage + 1),
            SchoolUsersPageButton.Ellipsis,
            SchoolUsersPageButton.Number(pageCount)
        )
    }
}

@Composable
private fun SchoolUsersCircleNavButton(
    icon: Painter,
    enabled: Boolean,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .size(28.dp)
            .background(SchoolUsersSurface, RoundedCornerShape(100))
            .border(1.dp, SchoolUsersBorder, RoundedCornerShape(100))
            .clickable(enabled = enabled, onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Icon(
            painter = icon,
            contentDescription = null,
            tint = if (enabled) SchoolUsersMuted else SchoolUsersBorder,
            modifier = Modifier.size(16.dp)
        )
    }
}

@Composable
internal fun SchoolUsersBanner(
    message: String,
    onDismiss: () -> Unit
) {
    AdminInfoBanner(
        message = message,
        onDismiss = onDismiss,
        leadingIcon = painterResource(Res.drawable.circle_check),
        backgroundColor = Color(0xFFF3F7FF),
        borderColor = Color(0xFFD8E4FF),
        contentColor = SchoolUsersPrimary,
        accentColor = SchoolUsersPrimary
    )
}
